import { makeObservable, observable, action, runInAction } from 'mobx';
import { searchSkills, getSkillById } from '@/api/skills';
import type { AiSkill } from '@/api/types';
import type { RootStore } from './RootStore';

// Merchant-scoped catalog of MAF procedural skills (ai.AiSkill rows).
// Skills are global-by-name today (no MerchantId column on ai.AiSkill), but the
// UI lives inside merchant scope. Filter / search happens here.

export class SkillStore {
  @observable skills: AiSkill[] = [];
  @observable totalCount = 0;
  @observable isLoading = false;
  @observable error: string | null = null;

  @observable search = '';
  @observable category: string | null = null;
  @observable agentId: number | null = null;
  @observable activeOnly = true;
  // Client-side filter that hides skills currently linked to >= 1 agent.
  // Drives the "Orphaned" stat tile when toggled on. Doesn't roundtrip to
  // the server because agentLinkCount is already in the search response —
  // a client filter is faster than re-fetching.
  @observable orphanedOnly = false;

  // Single-skill detail cache. Keyed by id so revisiting a detail page is instant.
  @observable detailCache = new Map<number, AiSkill>();
  @observable detailLoadingId: number | null = null;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
  }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await searchSkills({
        search: this.search,
        category: this.category ?? undefined,
        agentId: this.agentId ?? undefined,
        activeOnly: this.activeOnly,
        pageNo: 1,
        pageSize: 200,
      });
      runInAction(() => {
        this.skills = result.data ?? [];
        this.totalCount = result.paging?.totalRecordCount ?? this.skills.length;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load skills';
      });
    }
  }

  @action
  setSearch(value: string): void {
    this.search = value;
  }

  @action
  setCategory(value: string | null): void {
    this.category = value;
  }

  @action
  setAgentId(value: number | null): void {
    this.agentId = value;
  }

  @action
  setActiveOnly(value: boolean): void {
    this.activeOnly = value;
  }

  @action
  setOrphanedOnly(value: boolean): void {
    this.orphanedOnly = value;
  }

  /**
   * Snap to a single tile-driven filter state. Clears mutually-exclusive
   * filters so clicking a tile feels like "drill into this subset" rather
   * than "stack one more filter on top." Pure setter — caller is
   * responsible for re-fetching where needed.
   */
  @action
  applyTileFilter(tile: 'all' | 'active' | 'safety' | 'orphaned'): void {
    switch (tile) {
      case 'all':
        // Show everything — clear mutually-exclusive subset filters but
        // leave the search box and agent picker alone (those are user-
        // typed input, the user wouldn't expect them cleared).
        this.category = null;
        this.activeOnly = false;
        this.orphanedOnly = false;
        break;
      case 'active':
        this.activeOnly = true;
        this.orphanedOnly = false;
        break;
      case 'safety':
        this.category = 'Clinical Safety';
        this.orphanedOnly = false;
        break;
      case 'orphaned':
        // Server doesn't have an "orphaned" filter; we apply it client-side
        // by hiding rows whose agentLinkCount > 0. Clear the agent filter
        // because filtering by agent + orphaned is a contradiction (an
        // orphaned skill is linked to NO agents, but the agent filter
        // requires a linked agent).
        this.agentId = null;
        this.orphanedOnly = true;
        break;
    }
  }

  @action
  async loadDetail(id: number, force = false): Promise<AiSkill | null> {
    if (!force && this.detailCache.has(id)) {
      return this.detailCache.get(id) ?? null;
    }
    this.detailLoadingId = id;
    try {
      const skill = await getSkillById(id);
      runInAction(() => {
        this.detailCache.set(id, skill);
        this.detailLoadingId = null;
      });
      return skill;
    } catch (err) {
      runInAction(() => {
        this.detailLoadingId = null;
        this.error = err instanceof Error ? err.message : 'Failed to load skill';
      });
      return null;
    }
  }

  @action
  invalidateDetail(id: number): void {
    this.detailCache.delete(id);
  }
}
